/**
 * Cookie Consent Banner
 *
 * Bottom-sticky banner shown when user hasn't given consent.
 * Features:
 * - Accept All / Customize buttons
 * - Tailwind styling with dark/light theme support
 * - Mobile-friendly (doesn't block navigation)
 * - Animated entry
 */

import { useCookieConsent } from '../../contexts/CookieConsentContext'
import { Cookie } from 'lucide-react'

export default function CookieBanner() {
  const { showBanner, acceptAll, openModal } = useCookieConsent()

  if (!showBanner) return null

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-describedby="cookie-banner-description"
      className="fixed bottom-0 left-0 right-0 z-[100] animate-slide-up"
    >
      {/* Gradient fade effect at top */}
      <div className="h-5 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent pointer-events-none" />

      {/* Main banner content */}
      <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-gray-200 dark:border-white/10 shadow-lg dark:shadow-2xl dark:shadow-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-6">
            {/* Icon and text */}
            <div className="flex items-start sm:items-center gap-3.5 flex-1 min-w-0">
              <div className="flex-shrink-0 w-11 h-11 rounded-full bg-brand-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Cookie className="w-5.5 h-5.5 text-brand-600 dark:text-amber-400" />
              </div>
              <div id="cookie-banner-description">
                <p className="text-base leading-6 text-gray-700 dark:text-slate-300">
                  Help us improve Optioneer with privacy-friendly analytics.{' '}
                  <span className="text-gray-500 dark:text-slate-400">
                    No ads, no selling data. You can reject or customize anytime.
                  </span>{' '}
                  <a
                    href="/cookies"
                    className="text-brand-600 dark:text-amber-400 hover:underline font-medium"
                    aria-label="Learn more about our cookie policy"
                  >
                    Learn more
                  </a>
                </p>
              </div>
            </div>

            {/* Buttons: Customize | Accept All */}
            <div className="grid grid-cols-2 gap-3 w-full sm:flex sm:w-auto sm:flex-shrink-0">
              <button
                onClick={openModal}
                className="min-h-11 px-5 py-2.5 text-base font-medium text-brand-600 dark:text-amber-400 hover:text-brand-700 dark:hover:text-amber-300 hover:bg-brand-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors border border-brand-200 dark:border-amber-700/50"
                aria-label="Customize cookie preferences"
              >
                Customize
              </button>

              <button
                onClick={acceptAll}
                className="min-h-11 px-5 py-2.5 text-base font-medium text-white bg-brand-600 hover:bg-brand-700 dark:bg-amber-500 dark:hover:bg-amber-600 rounded-lg transition-colors shadow-sm"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
